import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Box, Chip, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import LeadSidebar from './LeadSidebar';
import LeadPipeline from './LeadPipeline';
import LeadTabs from './LeadTabs';
import PreferenseSidebar from './PreferenseSidebar';
import { useLeadDetails } from '../../../contexts/LeadDetailsContext';
import { fetchLeadsDefaultFilters } from '../../../services/common';
import NotFoundPage from '../../../components/common/404Page';
import ForbiddenPage from '../../../components/common/403';

// ─── Design tokens ────────────────────────────────────────────────────────────
const COLORS = {
  border: '#F1F5F9',
  bg:     '#F8FAFC',
  white:  '#FFFFFF',
  slate:  '#0F172A',
  slate2: '#1E293B',
  slate3: '#64748B',
  slate4: '#94A3B8',
  teal:   '#0E9F7E',
  tealLight: '#ECFDF5',
  tealDark:  '#065F46',
  red:    '#EF4444',
  redLight:'#FEF2F2',
  amber:  '#D97706',
  amberLight: '#FFFBEB',
  blue:   '#2563EB',
  blueLight:'#EFF6FF',
};

// Column widths
const LEFT_W  = 272; // Left sidebar
const RIGHT_W = 224; // Right preferences panel

const getTone = (score) => {
  if (score >= 70) return { color: COLORS.tealDark, bg: COLORS.tealLight, border: '#A7F3D0', label: 'Good' };
  if (score >= 40) return { color: '#92400E', bg: COLORS.amberLight, border: '#FDE68A', label: 'Needs attention' };
  return { color: '#991B1B', bg: COLORS.redLight, border: '#FECACA', label: 'At risk' };
};

const AIDecisionHeader = ({ lead, stats, overview }) => {
  const model = useMemo(() => {
    const temperature = stats?.temperature ?? 30;
    const engagement  = stats?.engagement_score ?? 7;
    const agingDays   = stats?.lead_age_days ?? 2;
    const budgetScore = stats?.budget_score ?? 15;
    const response    = stats?.response_score ?? 0;

    // Simple deterministic heuristic (UI-only) — keeps functionality intact.
    // Weighted: budget + intent/temperature + engagement - aging.
    const raw = (budgetScore * 4) + (temperature * 0.4) + (engagement * 0.3) - (agingDays * 2) - (response <= 0 ? 8 : 0);
    const probability = Math.max(5, Math.min(95, Math.round(raw)));
    const tone = getTone(probability);

    const flags = [
      response <= 0 ? { label: 'No Response', tone: 'risk' } : null,
      engagement < 25 ? { label: 'Low Engagement', tone: 'warn' } : null,
      budgetScore >= 15 ? { label: 'Budget Match', tone: 'good' } : null,
    ].filter(Boolean);

    const why = [
      response <= 0 ? 'No replies / no connected calls recorded recently.' : 'Some response signals exist.',
      engagement < 25 ? 'Engagement score is low; requires more touchpoints.' : 'Engagement is improving.',
      budgetScore >= 15 ? 'Budget appears within acceptable range (budget-fit signal).' : 'Budget fit is not yet confirmed.',
    ].slice(0, 3);

    const recommended =
      engagement < 25 || response <= 0
        ? 'Call now; if not reachable, send WhatsApp with 2 options and one question.'
        : 'Qualify quickly (budget, location, timeline) and lock a site visit.';

    const tags = [
      overview?.upcoming_task ? 'Pending Followups' : null,
      agingDays >= 7 ? 'Dormant' : null,
      engagement < 25 ? 'Low Engagement' : null,
    ].filter(Boolean);

    return { probability, tone, flags, why, recommended, tags };
  }, [stats, overview]);

  return (
    <Box sx={{ px: '20px', pb: '14px' }}>
      <Box
        sx={{
          border: `1px solid ${COLORS.border}`,
          borderRadius: '12px',
          bgcolor: COLORS.white,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: '16px', py: '12px', borderBottom: `1px solid ${COLORS.border}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 800, color: COLORS.slate4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                AI Decision
              </Typography>
              <Typography sx={{ fontSize: '16px', fontWeight: 900, color: model.tone.color, mt: '2px' }}>
                Conversion Probability {model.probability}%
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: model.tone.bg,
                color: model.tone.color,
                border: `1px solid ${model.tone.border}`,
                px: '10px',
                py: '6px',
                borderRadius: '999px',
                fontSize: '11px',
                fontWeight: 900,
                whiteSpace: 'nowrap',
              }}
            >
              {model.tone.label}
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: '16px', py: '12px' }}>
          {/* Why */}
          <Typography sx={{ fontSize: '11px', fontWeight: 800, color: COLORS.slate2, mb: '6px' }}>
            Why this score
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: '16px' }}>
            {model.why.map((t, i) => (
              <Box key={i} component="li" sx={{ fontSize: '12px', color: COLORS.slate3, lineHeight: 1.5, mb: '2px' }}>
                {t}
              </Box>
            ))}
          </Box>

          {/* Recommended action */}
          <Box sx={{ mt: '10px', p: '10px', borderRadius: '10px', bgcolor: COLORS.bg, border: `1px solid ${COLORS.border}` }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 900, color: COLORS.slate4, textTransform: 'uppercase', letterSpacing: '0.08em', mb: '4px' }}>
              Recommended action
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: COLORS.slate2, lineHeight: 1.4 }}>
              {model.recommended}
            </Typography>
          </Box>

          {/* Flags + dashboard tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', mt: '10px' }}>
            {model.flags.map((f) => {
              const tone =
                f.tone === 'good'
                  ? { bg: COLORS.tealLight, color: COLORS.tealDark, border: '#A7F3D0' }
                  : f.tone === 'warn'
                    ? { bg: COLORS.amberLight, color: '#92400E', border: '#FDE68A' }
                    : { bg: COLORS.redLight, color: '#991B1B', border: '#FECACA' };
              return (
                <Chip
                  key={f.label}
                  label={f.label}
                  size="small"
                  sx={{
                    height: 26,
                    fontSize: '0.72rem',
                    fontWeight: 900,
                    borderRadius: '999px',
                    bgcolor: tone.bg,
                    color: tone.color,
                    border: `1px solid ${tone.border}`,
                  }}
                />
              );
            })}

            {model.tags.map((t) => (
              <Chip
                key={t}
                label={t}
                size="small"
                sx={{
                  height: 26,
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  borderRadius: '999px',
                  bgcolor: COLORS.blueLight,
                  color: COLORS.blue,
                  border: `1px solid #BFDBFE`,
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const LeadDetailsContent = React.memo(() => {
  const { id: leadId } = useParams();

  const [leadsDefaultFilters, setLeadsDefaultFilters] = useState(null);
  const [openTaskModal, setOpenTaskModal]             = useState(false);

  const {
    loadAllLeadData,
    fetchLeadDetails,
    loading,
    leadDetails,
    error,
    resetState,
    stageStatusFlow,
    overview,
    stats,
  } = useLeadDetails();

  const initialized = useRef(false);

  // ── Data loading ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!initialized.current && leadId) {
      loadAllLeadData(leadId);
      initialized.current = true;
    }
  }, [leadId]);

  useEffect(() => {
    return () => {
      if (initialized.current) {
        resetState();
        initialized.current = false;
      }
    };
  }, []);

  useEffect(() => {
    if (leadsDefaultFilters === null) {
      fetchLeadsDefaultFilters().then(setLeadsDefaultFilters);
    }
  }, []);

  // fetchLeadDetails has no initialization guard — safe to call after any mutation
  const handleLeadUpdate = () => {
    if (leadId) fetchLeadDetails(leadId);
  };

  // ── Error / loading states ────────────────────────────────────────────────────
  const isLoading = loading.details || loading.stats;

  const is403Error = error.details && (
    (typeof error.details === 'object' && (error.details.is403 || error.details.status === 403)) ||
    (typeof error.details === 'string' && (
      error.details.includes('403') ||
      error.details.includes('Forbidden') ||
      error.details.includes('permission')
    ))
  );

  const is404Error = error.details && (
    (typeof error.details === 'object' && (error.details.is404 || error.details.status === 404)) ||
    (typeof error.details === 'string' && (
      error.details.includes('404') ||
      error.details.includes('Not Found') ||
      error.details.includes('Lead not found')
    ))
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoading && is403Error) {
    return <ForbiddenPage resource="Lead" />;
  }

  if (!isLoading && is404Error) {
    return (
      <NotFoundPage
        title="Lead Not Found"
        message="The lead you're looking for doesn't exist or has been removed."
        customBackPath="/leads"
        customBackText="Go Back to Leads"
      />
    );
  }

  // ── Layout: LEFT sidebar | CENTER (pipeline + tabs+right-panel) ──────────────
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        maxHeight: '100vh',
        overflow: 'hidden',
        bgcolor: COLORS.bg,
      }}
    >

      {/* ── LEFT COLUMN: Contact / AI / Quick Actions ────────────────────────── */}
      <Box
        sx={{
          width: LEFT_W,
          minWidth: LEFT_W,
          flexShrink: 0,
          height: '100%',
          overflowY: 'auto',
          bgcolor: COLORS.white,
          borderRight: `1px solid ${COLORS.border}`,
          '&::-webkit-scrollbar': { width: '3px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: COLORS.border, borderRadius: '4px' },
        }}
      >
        <LeadSidebar
          key={leadId}
          leadId={leadId}
          leadsDefaultFilters={leadsDefaultFilters}
          lead={leadDetails}
          variant="left"
        />
      </Box>

      {/* ── CENTER + RIGHT area: Pipeline on top, then Tabs | PreferenceSidebar ── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          bgcolor: COLORS.white,
        }}
      >
        {/* Pipeline bar — fixed height, never scrolls */}
        <Box
          sx={{
            flexShrink: 0,
            mb: '20px',
            borderBottom: `1px solid ${COLORS.border}`,
            bgcolor: COLORS.white,
          }}
        >
          <LeadPipeline
            lead={leadDetails}
            stageStatusFlow={stageStatusFlow || []}
          />
        </Box>

        {/* AI Decision header (dashboard) */}
        {/* <AIDecisionHeader lead={leadDetails} stats={stats} overview={overview} /> */}

        {/* Tabs + Right Preferences Panel — side by side, fills remaining height */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'row',
            overflow: 'hidden',
            gap: '0px',
          }}
        >
          {/* LeadTabs — scrollable center content */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              // Let the tab header scroll horizontally when it overflows.
              // Tab content manages its own vertical scroll.
              overflowX: 'auto',
              overflowY: 'hidden',
              border: 'none',
              // Slim scrollbar (horizontal) to match left sidebar.
              '&::-webkit-scrollbar': {
                height: '3px',
                width: '3px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: COLORS.border,
                borderRadius: '4px',
              },
              bgcolor: COLORS.white,
            }}
            className="smart-table-wrap"
          >
            <LeadTabs
              leadId={leadId}
              openTaskModal={openTaskModal}
              setOpenTaskModal={setOpenTaskModal}
              lead={leadDetails}
              onLeadUpdate={handleLeadUpdate}
              leadsDefaultFilters={leadsDefaultFilters}
            />
          </Box>

          {/* RIGHT COLUMN: Preferences / Budget / Tags etc. */}
          {/* <Box
            sx={{
              width: RIGHT_W,
              minWidth: RIGHT_W,
              flexShrink: 0,
              height: '100%',
              overflowY: 'auto',
              bgcolor: COLORS.white,
              borderLeft: `1px solid ${COLORS.border}`,
              '&::-webkit-scrollbar': { width: '3px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: COLORS.border, borderRadius: '4px' },
            }}
          >
            <PreferenseSidebar
              key={leadId}
              leadId={leadId}
              leadsDefaultFilters={leadsDefaultFilters}
              lead={leadDetails}
              variant="right"
            />
          </Box> */}
        </Box>
      </Box>

    </Box>
  );
});

export default LeadDetailsContent;
